import { Container, Graphics } from 'pixi.js';
import { darken, lighten, mix, type ThemeArt } from './theme-art';
import { bodyRect, bodyEllipse } from './shading';

/**
 * CASTLE HEARTHHOLD — the OUTDOOR / UNDERCROFT / TOWER art kit (ADR-0051, companion to {@link
 * './castle-kit'}). Where the indoor kit dresses the keep's halls, this file gives us the world OUTSIDE
 * and BELOW the great rooms: the Grand Courtyard (fountain · gardens · orchard · maze hedges), the Bailey
 * market, the rampart wall-walk, the catacombs/dungeon undercroft, and the three high towers (astronomy ·
 * mage · alchemy · the royal solar). Same convention as the rest of the catalog: each piece is a Pixi
 * {@link Container} in LOCAL space (footprint centred on its placement point, body rising ABOVE it toward
 * −y), with a soft contact shadow, the unified TOP-LEFT key light (see {@link './shading'}), and everything
 * themed off {@link ThemeArt} so the grounds re-skin with the palette. Built ONCE on map build/re-skin —
 * never per frame, never allocating in the loop (Performance-Engineer gate).
 *
 * The honeyed-warm storybook tone is kept dominant — but the OUTDOORS is the ONE place GREEN is welcome:
 * gardens, orchard trees, clipped hedges and topiary use natural WARM greens (mixed toward the wall hue,
 * never neon), and WATER reads as a soft, gently-shimmering blue. Light-emitting props (lamppost, gazebo,
 * crypt candle, crystal orb, alembic, spell circle) lay a cheap STATIC glow pool on the floor exactly like
 * the indoor kit, so the grounds still feel alive at low population.
 */

// ── Palette (theme-reactive — copied verbatim from castle-kit.ts so the two kits match) ───────────────
const stone = (art: ThemeArt): number => mix(0x6f6554, art.wall, 0.5);
const stoneLo = (art: ThemeArt): number => darken(stone(art), 0.16);
const stoneHi = (art: ThemeArt): number => lighten(stone(art), 0.12);
const wood = (art: ThemeArt): number => mix(0x6a4326, art.wall, 0.14);
const woodDk = (art: ThemeArt): number => darken(wood(art), 0.22);
const iron = (): number => 0x33333c;
const gold = (art: ThemeArt): number => mix(0xd9a63a, art.accent, 0.5);
const FLAME_OUT = 0xff7a2a;
const FLAME_MID = 0xffc24a;
const FLAME_CORE = 0xfff0b8;

// ── Outdoor naturals (warm greens + soft water — the ONE place green is welcome) ──────────────────────
/** A natural WARM green (foliage), mixed toward the wall hue so it sits in the honeyed palette — never neon. */
const leaf = (art: ThemeArt): number => mix(0x4a7a3a, art.wall, 0.12);
const leafHi = (art: ThemeArt): number => lighten(leaf(art), 0.18);
const leafLo = (art: ThemeArt): number => darken(leaf(art), 0.16);
/** Soft pond/fountain WATER — a gentle blue, lifted a touch so it reads as bright, calm water (not ink). */
const water = (art: ThemeArt): number => mix(0x5a86b0, art.accent, 0.1);
const waterHi = (art: ThemeArt): number => lighten(water(art), 0.22);

/** A soft elliptical contact shadow so a piece reads as resting on the floor (not floating). */
function shadow(w: number, h: number, y = 0): Graphics {
  return new Graphics().ellipse(0, y, w, h).fill({ color: 0x000000, alpha: 0.22 });
}

/** A static warm GLOW POOL on the floor (additive-feel: stacked low-alpha warm fills), scaled per theme. */
export function glowPool(c: Container, x: number, y: number, r: number, art: ThemeArt, k = 1): void {
  const a = art.glowStrength * k;
  c.addChild(
    new Graphics()
      .ellipse(x, y, r, r * 0.58)
      .fill({ color: art.glow, alpha: 0.06 * a + 0.03 })
      .ellipse(x, y, r * 0.62, r * 0.36)
      .fill({ color: art.glow, alpha: 0.09 * a + 0.04 })
      .ellipse(x, y, r * 0.3, r * 0.18)
      .fill({ color: lighten(art.glow, 0.25), alpha: 0.12 * a + 0.05 }),
  );
}

/** A cheap STATIC COOL glow pool (magic/water/crystal) — same shape as {@link glowPool} but a chosen hue. */
function coolPool(c: Container, x: number, y: number, r: number, tint: number, k = 1): void {
  c.addChild(
    new Graphics()
      .ellipse(x, y, r, r * 0.58)
      .fill({ color: tint, alpha: 0.06 * k + 0.03 })
      .ellipse(x, y, r * 0.62, r * 0.36)
      .fill({ color: tint, alpha: 0.1 * k + 0.04 })
      .ellipse(x, y, r * 0.3, r * 0.18)
      .fill({ color: lighten(tint, 0.3), alpha: 0.14 * k + 0.05 }),
  );
}

/** A little upward flame (base at y, licks rising above it). Three stacked teardrops orange→core. */
function flame(g: Graphics, x: number, y: number, s: number): void {
  g.ellipse(x, y - s * 0.5, s * 0.62, s * 1.0).fill({ color: FLAME_OUT, alpha: 0.95 });
  g.ellipse(x, y - s * 0.35, s * 0.4, s * 0.7).fill({ color: FLAME_MID, alpha: 0.95 });
  g.ellipse(x, y - s * 0.22, s * 0.2, s * 0.42).fill({ color: FLAME_CORE, alpha: 0.95 });
}

/** Concentric shimmer rings on a still water body (a couple of pale ripple ellipses). Cheap calm-water tell. */
function shimmer(g: Graphics, art: ThemeArt, cx: number, cy: number, rx: number, ry: number): void {
  g.ellipse(cx, cy, rx * 0.72, ry * 0.72).stroke({ color: waterHi(art), width: 1.5, alpha: 0.3 });
  g.ellipse(cx, cy, rx * 0.44, ry * 0.44).stroke({ color: waterHi(art), width: 1.2, alpha: 0.22 });
}

// ── OUTDOOR / GARDEN / COURTYARD ──────────────────────────────────────────────────────────────────────

/** A tiered round STONE FOUNTAIN: a wide basin of soft-blue water + a central pillar + an upper bowl, with
 *  falling-water streaks and a faint cool shimmer pool. The Grand Courtyard's hero centrepiece. */
export function fountain(art: ThemeArt, r = 70): Container {
  const c = new Container();
  coolPool(c, 0, 6, r * 1.2, waterHi(art), 0.7);
  c.addChild(shadow(r, r * 0.42, r * 0.32));
  const g = new Graphics();
  // wide stone basin rim
  g.ellipse(0, 4, r, r * 0.5).fill(stoneLo(art));
  bodyEllipse(g, 0, -2, r, r * 0.48, stone(art));
  // the pool of water inside the rim
  g.ellipse(0, -2, r - 12, (r - 12) * 0.46).fill({ color: water(art), alpha: 0.92 });
  g.ellipse(0, -2, r - 12, (r - 12) * 0.46).fill({ color: waterHi(art), alpha: 0.12 });
  shimmer(g, art, 0, -2, r - 12, (r - 12) * 0.46);
  // central stone pillar
  bodyRect(g, -10, -34, 20, 38, 4, stone(art));
  g.rect(-10, -34, 20, 5).fill({ color: stoneHi(art), alpha: 0.6 });
  // upper bowl + the water it holds
  g.ellipse(0, -38, 26, 11).fill(stoneLo(art));
  bodyEllipse(g, 0, -42, 26, 10, stone(art));
  g.ellipse(0, -42, 19, 6).fill({ color: water(art), alpha: 0.92 });
  g.ellipse(0, -44, 12, 3.5).fill({ color: waterHi(art), alpha: 0.5 });
  // a little finial jet at the very top
  g.roundRect(-2, -52, 4, 8, 2).fill(stoneHi(art));
  c.addChild(g);
  // falling-water streaks from the upper bowl into the basin
  const w = new Graphics();
  for (const sx of [-20, -7, 7, 20]) {
    w.roundRect(sx - 1, -40, 2, 30, 1).fill({ color: waterHi(art), alpha: 0.5 });
  }
  // a soft splash ring where the jet lands
  w.ellipse(0, -2, 16, 7).stroke({ color: waterHi(art), width: 1.5, alpha: 0.4 });
  c.addChild(w);
  return c;
}

/** A TREE: brown trunk + a big rounded canopy. `oak` = broad green with dappled highlights; `pine` = stacked
 *  conical green tiers; `blossom` = soft pink-white spring canopy. Orchard / courtyard / garden. */
export function tree(art: ThemeArt, kind: 'oak' | 'pine' | 'blossom' = 'oak', h = 120): Container {
  const c = new Container();
  c.addChild(shadow(h * 0.26, h * 0.1, h * 0.06));
  const g = new Graphics();
  const trunkW = Math.max(8, h * 0.1);
  const trunkH = h * 0.4;
  // trunk (a touch wider at the base)
  g.moveTo(-trunkW / 2, 0).lineTo(trunkW / 2, 0).lineTo(trunkW * 0.34, -trunkH).lineTo(-trunkW * 0.34, -trunkH).fill(wood(art));
  g.moveTo(-trunkW * 0.34, -trunkH).lineTo(-trunkW * 0.18, -trunkH).lineTo(-trunkW * 0.3, 0).lineTo(-trunkW / 2, 0).fill({ color: 0xffffff, alpha: 0.08 });
  c.addChild(g);
  const canopy = new Graphics();
  if (kind === 'pine') {
    // stacked conical tiers, dark→lit
    const baseW = h * 0.42;
    for (let i = 0; i < 4; i++) {
      const ty = -trunkH - i * (h * 0.16);
      const tw = baseW * (1 - i * 0.2);
      canopy.poly([0, ty - h * 0.2, tw / 2, ty, -tw / 2, ty]).fill(i % 2 === 0 ? leaf(art) : leafLo(art));
      canopy.poly([0, ty - h * 0.2, tw * 0.16, ty - h * 0.08, -tw * 0.04, ty - h * 0.04]).fill({ color: leafHi(art), alpha: 0.4 });
    }
  } else if (kind === 'blossom') {
    // soft pink-white spring canopy: clustered pale blooms over a faint green
    const cy = -trunkH - h * 0.22;
    const rx = h * 0.34;
    canopy.ellipse(0, cy, rx, rx * 0.86).fill({ color: mix(0xf7c9d8, art.wall, 0.06) });
    canopy.ellipse(0, cy, rx, rx * 0.86).fill({ color: leaf(art), alpha: 0.12 });
    for (const [bx, by, br] of [[-rx * 0.5, -rx * 0.3, 0.34], [rx * 0.45, -rx * 0.4, 0.4], [0, -rx * 0.62, 0.36], [rx * 0.2, rx * 0.1, 0.3], [-rx * 0.35, rx * 0.2, 0.32]] as const) {
      canopy.ellipse(bx, cy + by, rx * br, rx * br * 0.86).fill({ color: 0xfdeef3, alpha: 0.85 });
    }
    canopy.ellipse(-rx * 0.3, cy - rx * 0.3, rx * 0.3, rx * 0.24).fill({ color: 0xffffff, alpha: 0.4 }); // lit cap
  } else {
    // oak: a broad rounded green crown with lighter dappled highlights
    const cy = -trunkH - h * 0.18;
    const rx = h * 0.36;
    canopy.ellipse(0, cy, rx, rx * 0.9).fill(leaf(art));
    canopy.ellipse(-rx * 0.4, cy + rx * 0.2, rx * 0.6, rx * 0.55).fill({ color: leafLo(art), alpha: 0.7 }); // shaded lobe
    canopy.ellipse(rx * 0.3, cy + rx * 0.25, rx * 0.5, rx * 0.45).fill(leaf(art));
    // dappled highlights
    for (const [dx, dy, dr] of [[-rx * 0.3, -rx * 0.35, 0.26], [rx * 0.25, -rx * 0.2, 0.22], [0, -rx * 0.1, 0.2], [-rx * 0.1, rx * 0.2, 0.18]] as const) {
      canopy.ellipse(dx, cy + dy, rx * dr, rx * dr * 0.86).fill({ color: leafHi(art), alpha: 0.45 });
    }
  }
  c.addChild(canopy);
  return c;
}

/** A clipped green HEDGE block — rounded top, a lit upper edge, a slightly darker base. Maze walls / borders. */
export function hedge(art: ThemeArt, w = 120, h = 40): Container {
  const c = new Container();
  c.addChild(shadow(w / 2, h * 0.22, h * 0.5));
  const g = new Graphics();
  // body with a rounded top
  g.roundRect(-w / 2, -h, w, h + 6, Math.min(14, h * 0.4)).fill(leaf(art));
  g.roundRect(-w / 2, h - h - 0, w, h * 0.34, Math.min(14, h * 0.4)).fill({ color: leafLo(art), alpha: 0.6 }); // darker base
  // lit upper edge + a few clipped-leaf dabs so it reads as foliage, not a slab
  g.roundRect(-w / 2, -h, w, h * 0.28, Math.min(14, h * 0.4)).fill({ color: leafHi(art), alpha: 0.4 });
  for (let x = -w / 2 + 8; x < w / 2 - 6; x += 16) {
    g.ellipse(x, -h + 5, 7, 5).fill({ color: leafHi(art), alpha: 0.3 });
    g.ellipse(x + 8, -h * 0.5, 6, 6).fill({ color: leafLo(art), alpha: 0.25 });
  }
  c.addChild(g);
  return c;
}

/** A sculpted TOPIARY shrub on a small pot/base — a clipped `ball` or `cone`. Garden ornament. */
export function topiary(art: ThemeArt, shape: 'ball' | 'cone' = 'ball'): Container {
  const c = new Container();
  c.addChild(shadow(16, 6, 18));
  const g = new Graphics();
  // terracotta pot
  g.moveTo(-13, 2).lineTo(13, 2).lineTo(9, 18).lineTo(-9, 18).fill(mix(0xa5663a, art.wall, 0.12));
  g.roundRect(-14, -2, 28, 5, 2).fill(lighten(mix(0xa5663a, art.wall, 0.12), 0.12)); // pot lip
  // short stem
  g.rect(-2, -8, 4, 12).fill(woodDk(art));
  if (shape === 'cone') {
    g.poly([0, -54, 16, -6, -16, -6]).fill(leaf(art));
    g.poly([0, -54, 5, -30, -6, -20]).fill({ color: leafHi(art), alpha: 0.4 }); // lit edge
    g.poly([0, -54, 14, -8, 4, -8]).fill({ color: leafLo(art), alpha: 0.35 }); // shaded
  } else {
    bodyEllipse(g, 0, -26, 19, 20, leaf(art));
    g.ellipse(-6, -32, 9, 8).fill({ color: leafHi(art), alpha: 0.4 }); // lit cap
    g.ellipse(6, -18, 10, 8).fill({ color: leafLo(art), alpha: 0.3 }); // shaded base
  }
  c.addChild(g);
  return c;
}

/** An open garden GAZEBO / pavilion: slim posts, a peaked domed roof with a finial, a shaded round floor,
 *  and a soft warm glow beneath. Ducks gather inside. */
export function gazebo(art: ThemeArt, r = 70): Container {
  const c = new Container();
  glowPool(c, 0, 6, r * 0.9, art, 0.6);
  c.addChild(shadow(r, r * 0.42, r * 0.34));
  const g = new Graphics();
  // raised round floor
  g.ellipse(0, 8, r, r * 0.46).fill(stoneLo(art));
  bodyEllipse(g, 0, 2, r - 6, (r - 6) * 0.44, stone(art));
  g.ellipse(0, 2, r - 18, (r - 18) * 0.44).fill({ color: stoneHi(art), alpha: 0.3 });
  // six slim posts around the back/sides (front kept open so ducks read as "inside")
  const posts: Array<[number, number]> = [];
  for (let i = 0; i < 6; i++) {
    const a = Math.PI + (i / 5) * Math.PI; // back half-ring
    posts.push([Math.cos(a) * (r - 12), Math.sin(a) * (r - 12) * 0.44]);
  }
  for (const [px, py] of posts) g.roundRect(px - 2.5, py - 56, 5, 58, 2).fill(wood(art));
  c.addChild(g);
  // peaked domed roof
  const roof = new Graphics();
  roof.moveTo(-r + 4, -52).quadraticCurveTo(0, -52 - r * 0.5, r - 4, -52).quadraticCurveTo(0, -42, -r + 4, -52).fill(mix(0x8a4a3a, art.accent, 0.12));
  roof.moveTo(-r + 4, -52).quadraticCurveTo(0, -52 - r * 0.5, 0, -52 - r * 0.5).quadraticCurveTo(-r * 0.5, -58, -r + 4, -52).fill({ color: 0xffffff, alpha: 0.14 }); // lit side
  roof.moveTo(-r + 4, -52).quadraticCurveTo(0, -42, r - 4, -52).stroke({ color: gold(art), width: 2, alpha: 0.5 }); // eave trim
  // finial
  roof.roundRect(-2, -52 - r * 0.5 - 8, 4, 10, 2).fill(gold(art));
  roof.circle(0, -52 - r * 0.5 - 10, 4).fill(gold(art));
  c.addChild(roof);
  return c;
}

/** A still water POND (laid FLAT, decal-like): soft-blue with concentric shimmer rings, a reed/grass fringe,
 *  and a couple of lily pads. */
export function pond(art: ThemeArt, w = 160, h = 110): Container {
  const c = new Container();
  const g = new Graphics();
  // soft irregular water body (an ellipse reads as a calm pool)
  g.ellipse(0, 0, w / 2, h / 2).fill({ color: water(art), alpha: 0.9 });
  g.ellipse(0, 0, w / 2, h / 2).fill({ color: waterHi(art), alpha: 0.1 });
  g.ellipse(-w * 0.16, -h * 0.16, w * 0.22, h * 0.16).fill({ color: waterHi(art), alpha: 0.22 }); // lit patch
  shimmer(g, art, 0, 0, w / 2, h / 2);
  c.addChild(g);
  // reed / grass fringe around the rim
  const reeds = new Graphics();
  for (let a = 0; a < Math.PI * 2; a += Math.PI / 9) {
    const ex = Math.cos(a) * (w / 2 - 3);
    const ey = Math.sin(a) * (h / 2 - 3);
    const tip = a < Math.PI ? -10 : -7; // taller at the back
    reeds.moveTo(ex, ey).lineTo(ex + 2, ey + tip).stroke({ color: leafLo(art), width: 2, alpha: 0.7 });
    reeds.moveTo(ex - 2, ey).lineTo(ex - 3, ey + tip * 0.7).stroke({ color: leaf(art), width: 1.6, alpha: 0.6 });
  }
  c.addChild(reeds);
  // a couple of lily pads + a bloom
  const lily = new Graphics();
  for (const [lx, ly, lr] of [[-w * 0.18, h * 0.12, 12], [w * 0.22, -h * 0.06, 10], [w * 0.05, h * 0.2, 8]] as const) {
    lily.ellipse(lx, ly, lr, lr * 0.6).fill({ color: leaf(art), alpha: 0.9 });
    lily.moveTo(lx, ly).lineTo(lx + lr * 0.6, ly - lr * 0.3).stroke({ color: leafLo(art), width: 1, alpha: 0.5 }); // notch
    lily.ellipse(lx - lr * 0.3, ly - lr * 0.2, lr * 0.4, lr * 0.24).fill({ color: leafHi(art), alpha: 0.35 });
  }
  lily.circle(-w * 0.16, h * 0.1, 3).fill({ color: 0xf7d2e0, alpha: 0.9 }); // a single bloom
  c.addChild(lily);
  return c;
}

/** A small arched stone footBRIDGE (handrails + a visible arch underside) to cross the pond/stream. */
export function archBridge(art: ThemeArt, w = 120): Container {
  const c = new Container();
  c.addChild(shadow(w / 2, 10, 12));
  const g = new Graphics();
  const half = w / 2;
  const rise = w * 0.22;
  // the arch span (deck) — a gentle hump
  g.moveTo(-half, 8)
    .quadraticCurveTo(0, 8 - rise, half, 8)
    .lineTo(half, 16)
    .quadraticCurveTo(0, 16 - rise, -half, 16)
    .fill(stone(art));
  // lit top of the deck
  g.moveTo(-half, 8).quadraticCurveTo(0, 8 - rise, half, 8).quadraticCurveTo(0, 11 - rise, -half, 8).fill({ color: 0xffffff, alpha: 0.1 });
  // the arch underside (the opening the water passes through)
  g.moveTo(-half + 14, 22).quadraticCurveTo(0, 22 - rise * 0.7, half - 14, 22).quadraticCurveTo(0, 26, -half + 14, 22).fill(stoneLo(art));
  // plank seams across the deck
  for (let x = -half + 12; x < half - 8; x += 16) {
    const py = 6 - Math.round(rise * (1 - Math.pow((x / half), 2)));
    g.moveTo(x, py).lineTo(x, py + 8).stroke({ color: stoneLo(art), width: 1, alpha: 0.4 });
  }
  c.addChild(g);
  // handrails (posts + a curved top rail) on the near edge
  const rail = new Graphics();
  for (const rx of [-half + 6, -half * 0.4, half * 0.4, half - 6]) {
    const ry = 6 - Math.round(rise * (1 - Math.pow((rx / half), 2)));
    rail.roundRect(rx - 1.5, ry - 16, 3, 16, 1).fill(woodDk(art));
  }
  rail.moveTo(-half + 6, -10).quadraticCurveTo(0, -10 - rise, half - 6, -10).stroke({ color: wood(art), width: 3 });
  c.addChild(rail);
  return c;
}

/** An ornate park GARDEN BENCH — wrought-iron scrollwork ends + a slatted wooden seat/back. Distinct from
 *  the indoor wooden pew. */
export function gardenBench(art: ThemeArt, w = 64): Container {
  const c = new Container();
  c.addChild(shadow(w / 2, 6, 12));
  const g = new Graphics();
  const half = w / 2;
  // wrought-iron scrollwork ends
  for (const sx of [-half + 4, half - 4]) {
    g.moveTo(sx, 14).lineTo(sx, -18).stroke({ color: iron(), width: 2.5 });
    g.moveTo(sx, -18).quadraticCurveTo(sx + (sx < 0 ? 8 : -8), -22, sx + (sx < 0 ? 4 : -4), -14).stroke({ color: iron(), width: 2 }); // scroll
    g.circle(sx + (sx < 0 ? 5 : -5), -8, 3).stroke({ color: iron(), width: 1.5 }); // a curl
    g.moveTo(sx, 14).lineTo(sx + (sx < 0 ? 6 : -6), 16).stroke({ color: iron(), width: 2 }); // foot
  }
  // slatted seat
  for (const sy of [-2, 2, 6]) g.roundRect(-half + 4, sy, w - 8, 3, 1.5).fill(wood(art));
  // slatted back
  for (const sy of [-16, -11, -6]) g.roundRect(-half + 6, sy, w - 12, 3, 1.5).fill(darken(wood(art), 0.06));
  g.roundRect(-half + 4, -2, w - 8, 1.5, 1).fill({ color: 0xffffff, alpha: 0.14 }); // lit seat front edge
  c.addChild(g);
  return c;
}

/** A low soil FLOWER BED dotted with warm blooms (reds/yellows/whites/violet) + a few leaves, in a tidy
 *  stone border. */
export function flowerBed(art: ThemeArt, w = 90): Container {
  const c = new Container();
  c.addChild(shadow(w / 2, 8, 10));
  const g = new Graphics();
  const half = w / 2;
  // stone border + soil
  g.roundRect(-half, -10, w, 22, 4).fill(stoneLo(art));
  g.roundRect(-half + 4, -8, w - 8, 16, 3).fill(mix(0x4a3526, art.wall, 0.1)); // dark soil
  g.roundRect(-half + 4, -8, w - 8, 4, 3).fill({ color: 0x000000, alpha: 0.12 });
  c.addChild(g);
  // leaves first, then blooms scattered on top
  const f = new Graphics();
  const blooms = [0xd8453a, 0xe6b53a, 0xf4ead6, 0x9a55b0, 0xe6b53a, 0xd8453a];
  for (let i = 0; i < 14; i++) {
    const bx = -half + 8 + ((i * 53) % (w - 16));
    const by = -4 + ((i * 31) % 12) - 6;
    f.ellipse(bx + 3, by + 2, 4, 2.4).fill({ color: leaf(art), alpha: 0.85 }); // leaf
  }
  for (let i = 0; i < 12; i++) {
    const bx = -half + 10 + ((i * 71) % (w - 20));
    const by = -8 + ((i * 37) % 10);
    const col = blooms[i % blooms.length]!;
    f.circle(bx, by, 3.2).fill({ color: col, alpha: 0.95 });
    f.circle(bx - 0.8, by - 0.8, 1.2).fill({ color: lighten(col, 0.3), alpha: 0.6 });
    f.circle(bx, by, 1).fill({ color: gold(art), alpha: 0.7 }); // heart
  }
  c.addChild(f);
  return c;
}

/** A round stone wishing-WELL: a low wall + two posts + a peaked little roof + a bucket on a rope. */
export function well(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(shadow(28, 11, 16));
  const g = new Graphics();
  // round low wall
  g.ellipse(0, 8, 28, 13).fill(stoneLo(art));
  bodyEllipse(g, 0, 2, 28, 12, stone(art));
  g.ellipse(0, 2, 21, 8).fill({ color: 0x1c2228, alpha: 0.85 }); // dark shaft
  g.ellipse(0, 1, 21, 8).fill({ color: water(art), alpha: 0.25 }); // a glint of water far below
  // coursed-stone band on the wall
  for (let a = -2.4; a < 2.4; a += 0.5) g.moveTo(Math.cos(a) * 26, 4 + Math.sin(a) * 4).lineTo(Math.cos(a) * 26, 12 + Math.sin(a) * 4).stroke({ color: stoneLo(art), width: 1, alpha: 0.5 });
  c.addChild(g);
  // two posts + peaked roof + winch
  const r = new Graphics();
  for (const px of [-24, 24]) r.roundRect(px - 2.5, -44, 5, 48, 2).fill(woodDk(art));
  r.moveTo(-30, -42).lineTo(0, -64).lineTo(30, -42).lineTo(20, -42).lineTo(0, -56).lineTo(-20, -42).fill(mix(0x8a4a3a, art.accent, 0.1)); // roof
  r.moveTo(-30, -42).lineTo(0, -64).lineTo(-6, -60).lineTo(-20, -42).fill({ color: 0xffffff, alpha: 0.12 }); // lit slope
  r.moveTo(-24, -38).lineTo(24, -38).stroke({ color: woodDk(art), width: 3 }); // winch axle
  r.circle(-24, -38, 4).fill(iron());
  // rope + bucket
  r.moveTo(6, -38).lineTo(6, -12).stroke({ color: 0x8a7a4a, width: 1.5 });
  r.moveTo(2, -12).lineTo(10, -12).lineTo(9, -3).lineTo(3, -3).fill(woodDk(art)); // bucket
  r.rect(2, -12, 8, 2).fill({ color: gold(art), alpha: 0.5 });
  c.addChild(r);
  return c;
}

/** A MARKET STALL: a wood counter, two posts, a STRIPED awning (cloth defaults to a warm accent), and a few
 *  goods on top (sacks / fruit / pots). The Bailey market. */
export function marketStall(art: ThemeArt, cloth?: number): Container {
  const c = new Container();
  c.addChild(shadow(46, 11, 18));
  const stripe = cloth ?? mix(0xc0492f, art.accent, 0.12);
  const g = new Graphics();
  // counter
  bodyRect(g, -42, -6, 84, 16, 3, wood(art));
  bodyRect(g, -38, 10, 76, 12, 2, woodDk(art)); // apron
  g.rect(-42, -6, 84, 3).fill({ color: 0xffffff, alpha: 0.12 });
  // two posts
  for (const px of [-40, 40]) g.roundRect(px - 2.5, -58, 5, 54, 2).fill(woodDk(art));
  c.addChild(g);
  // striped awning (peaked) + scalloped fringe
  const a = new Graphics();
  a.moveTo(-48, -50).lineTo(0, -64).lineTo(48, -50).lineTo(48, -42).lineTo(-48, -42).fill(stripe);
  for (let i = 0; i < 8; i++) {
    if (i % 2 === 0) a.moveTo(-48 + i * 12, -52 + Math.abs(i - 4) * 1.5).lineTo(-48 + (i + 1) * 12, -52 + Math.abs(i + 1 - 4) * 1.5).lineTo(-48 + (i + 1) * 12, -42).lineTo(-48 + i * 12, -42).fill({ color: 0xf4ecd8, alpha: 0.85 }); // light stripes
  }
  for (let x = -48; x < 48; x += 12) a.poly([x, -42, x + 6, -34, x + 12, -42]).fill({ color: darken(stripe, 0.1), alpha: 0.9 }); // scallop fringe
  a.moveTo(-48, -50).lineTo(0, -64).lineTo(0, -50).fill({ color: 0xffffff, alpha: 0.12 }); // lit half
  c.addChild(a);
  // goods on the counter
  const goods = new Graphics();
  goods.poly([-34, -8, -22, -8, -24, -22, -32, -22]).fill(mix(0xb8a06a, art.wall, 0.15)); // sack
  goods.poly([-34, -8, -22, -8, -23, -14, -33, -14]).fill({ color: 0xffffff, alpha: 0.1 });
  for (const [fx, col] of [[-10, 0xd84a3a], [-2, 0xe6b53a], [6, 0x4a8a3a], [14, 0xd84a3a]] as const) goods.circle(fx, -12, 4).fill({ color: col, alpha: 0.95 }); // fruit
  bodyEllipse(goods, 28, -12, 9, 7, mix(0x8a5a3a, art.wall, 0.1)); // a pot
  goods.ellipse(28, -16, 6, 3).fill({ color: 0x2a1a12, alpha: 0.7 });
  c.addChild(goods);
  return c;
}

/** A tall wrought-iron LAMPPOST with a glowing lantern head (warm glow pool). Outdoor light. */
export function lamppost(art: ThemeArt): Container {
  const c = new Container();
  glowPool(c, 0, 4, 38, art, 0.95);
  c.addChild(shadow(12, 5, 14));
  const g = new Graphics();
  // base + fluted post
  g.ellipse(0, 14, 11, 4).fill(iron());
  g.roundRect(-4, 2, 8, 14, 2).fill(darken(iron(), 0.08));
  g.rect(-2.5, -46, 5, 48).fill(iron());
  g.rect(-2.5, -46, 2, 48).fill({ color: lighten(iron(), 0.25), alpha: 0.5 }); // lit edge
  // a little scroll bracket
  g.moveTo(0, -46).quadraticCurveTo(8, -50, 6, -56).stroke({ color: iron(), width: 2 });
  c.addChild(g);
  // glowing lantern head
  const l = new Graphics();
  l.moveTo(-10, -56).lineTo(10, -56).lineTo(7, -76).lineTo(-7, -76).fill(iron()); // cage frame
  l.moveTo(-8, -58).lineTo(8, -58).lineTo(6, -74).lineTo(-6, -74).fill({ color: lighten(art.glow, 0.3), alpha: 0.9 }); // lit pane
  l.moveTo(0, -73).lineTo(3, -60).lineTo(-3, -60).fill({ color: FLAME_MID, alpha: 0.8 }); // a little flame inside
  l.poly([-10, -76, 10, -76, 0, -84]).fill(iron()); // little roof
  l.circle(0, -85, 2).fill(iron()); // finial
  c.addChild(l);
  return c;
}

/** A stone SUNDIAL — a pedestal + a round dial plate with hour ticks + a gnomon. A garden ornament. */
export function sundial(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(shadow(16, 6, 16));
  const g = new Graphics();
  // pedestal
  g.ellipse(0, 14, 14, 5).fill(stoneLo(art));
  bodyRect(g, -7, -4, 14, 18, 3, stone(art));
  g.moveTo(-12, -4).lineTo(12, -4).lineTo(8, -10).lineTo(-8, -10).fill(stoneHi(art)); // capital
  c.addChild(g);
  // dial plate (a low round disc) + ticks
  const d = new Graphics();
  d.ellipse(0, -12, 18, 8).fill(mix(0x8a8a96, art.accent, 0.1));
  d.ellipse(0, -13, 16, 6.5).fill({ color: lighten(mix(0x8a8a96, art.accent, 0.1), 0.18) });
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    const ex = Math.cos(a) * 14;
    const ey = -13 + Math.sin(a) * 5.5;
    d.circle(ex, ey, 0.9).fill({ color: 0x3a3a44, alpha: 0.7 });
  }
  // gnomon (a triangular blade casting the hour shadow)
  d.poly([-2, -13, 9, -13, -2, -26]).fill({ color: darken(gold(art), 0.05), alpha: 0.95 });
  d.poly([-2, -13, 3, -13, -2, -21]).fill({ color: 0xffffff, alpha: 0.2 });
  c.addChild(d);
  return c;
}

/** A row of battlement CRENELLATION merlons (the rampart wall-walk parapet): alternating raised stone blocks
 *  with a lit top + the embrasure gaps between them. */
export function crenellation(art: ThemeArt, w = 160): Container {
  const c = new Container();
  const g = new Graphics();
  const half = w / 2;
  // the solid parapet wall band
  bodyRect(g, -half, -8, w, 22, 2, stone(art));
  g.rect(-half, 12, w, 3).fill(stoneLo(art)); // base line
  // alternating merlons rising above
  const unit = 26;
  for (let x = -half; x < half - 4; x += unit) {
    const mw = Math.min(unit * 0.6, half - x - 2);
    g.rect(x, -26, mw, 20).fill(stone(art));
    g.rect(x, -26, mw, 5).fill({ color: stoneHi(art), alpha: 0.7 }); // lit top
    g.rect(x, -10, mw, 3).fill({ color: 0x000000, alpha: 0.1 });
    // coursed seam down each merlon
    g.moveTo(x + mw / 2, -26).lineTo(x + mw / 2, -8).stroke({ color: stoneLo(art), width: 1, alpha: 0.35 });
  }
  c.addChild(g);
  return c;
}

// ── UNDERCROFT / DUNGEON / CATACOMBS ──────────────────────────────────────────────────────────────────

/** An iron prison-cell front (JAIL BARS): vertical bars + 2 cross-rails + a barred door outline + a lock.
 *  Dark iron with a cold highlight. Hang against a cell opening. */
export function jailBars(art: ThemeArt, w = 120, h = 120): Container {
  const c = new Container();
  void art;
  const g = new Graphics();
  const half = w / 2;
  const ironHi = lighten(iron(), 0.28);
  // top & bottom mounting rails
  g.rect(-half, -h / 2, w, 6).fill(iron());
  g.rect(-half, h / 2 - 6, w, 6).fill(darken(iron(), 0.1));
  // vertical bars
  for (let x = -half + 6; x <= half - 6; x += 14) {
    g.rect(x - 2, -h / 2 + 4, 4, h - 8).fill(iron());
    g.rect(x - 2, -h / 2 + 4, 1.4, h - 8).fill({ color: ironHi, alpha: 0.55 }); // cold highlight
  }
  // two cross-rails
  for (const ry of [-h / 6, h / 4]) g.rect(-half + 2, ry, w - 4, 4).fill(darken(iron(), 0.06));
  // the barred DOOR outline (a framed panel offset to one side)
  const dx = half * 0.1;
  g.rect(dx - half * 0.42, -h / 2 + 4, half * 0.84, h - 8).stroke({ color: ironHi, width: 2.5, alpha: 0.7 });
  g.rect(dx - half * 0.42, -h / 2 + 4, half * 0.84, h - 8).stroke({ color: darken(iron(), 0.2), width: 1, alpha: 0.6 });
  // a heavy lock plate on the door edge
  g.roundRect(dx + half * 0.34, h / 16, 12, 14, 2).fill(darken(iron(), 0.12));
  g.roundRect(dx + half * 0.34, h / 16, 12, 4, 2).fill({ color: ironHi, alpha: 0.5 });
  g.circle(dx + half * 0.34 + 6, h / 16 + 8, 2).fill(0x14141a); // keyhole
  c.addChild(g);
  return c;
}

/** Wall MANACLES — a small iron wall-plate with two hanging chains + open cuffs. Dungeon ambience. */
export function manacles(art: ThemeArt): Container {
  const c = new Container();
  void art;
  const g = new Graphics();
  const ironHi = lighten(iron(), 0.25);
  // wall plate with two rivets
  g.roundRect(-14, -14, 28, 12, 2).fill(darken(iron(), 0.08));
  g.roundRect(-14, -14, 28, 4, 2).fill({ color: ironHi, alpha: 0.5 });
  g.circle(-9, -8, 1.6).fill(0x14141a).circle(9, -8, 1.6).fill(0x14141a); // rivets
  // two hanging chains (a few links each)
  for (const cx of [-9, 9]) {
    let y = -2;
    for (let i = 0; i < 4; i++) {
      g.ellipse(cx, y, 2.4, 3.4).stroke({ color: iron(), width: 1.8 });
      g.ellipse(cx, y, 1.1, 1.6).stroke({ color: ironHi, width: 0.6, alpha: 0.5 });
      y += 6;
    }
    // open cuff at the end
    g.ellipse(cx, y + 2, 5, 4).stroke({ color: iron(), width: 2.5 });
    g.ellipse(cx, y + 2, 5, 4).stroke({ color: ironHi, width: 1, alpha: 0.4 });
  }
  c.addChild(g);
  return c;
}

/** A stone SARCOPHAGUS / tomb-coffin with a carved lid (a simple effigy line + a gold rim). Catacombs. */
export function sarcophagus(art: ThemeArt, w = 90): Container {
  const c = new Container();
  c.addChild(shadow(w / 2, 12, 18));
  const g = new Graphics();
  const half = w / 2;
  // tapered coffin body (wider at the "head")
  g.moveTo(-half, -4).lineTo(half, -2).lineTo(half - 4, 18).lineTo(-half + 2, 16).fill(stoneLo(art));
  // raised carved lid
  g.moveTo(-half + 2, -10).lineTo(half - 2, -8).lineTo(half - 6, 8).lineTo(-half + 4, 6).fill(stone(art));
  g.moveTo(-half + 2, -10).lineTo(half - 2, -8).lineTo(half - 4, -3).lineTo(-half + 3, -5).fill({ color: stoneHi(art), alpha: 0.6 }); // lit top
  // gold rim
  g.moveTo(-half + 2, -10).lineTo(half - 2, -8).lineTo(half - 6, 8).lineTo(-half + 4, 6).stroke({ color: gold(art), width: 2, alpha: 0.55 });
  // a simple carved effigy (a hooded figure with crossed arms) etched into the lid
  g.ellipse(-half * 0.5, -3, 6, 7).stroke({ color: stoneLo(art), width: 1.5, alpha: 0.6 }); // head/cowl
  g.moveTo(-half * 0.5 - 6, 3).quadraticCurveTo(0, 6, half * 0.55, 2).stroke({ color: stoneLo(art), width: 1.5, alpha: 0.5 }); // shoulder/robe
  g.moveTo(-half * 0.2, -1).lineTo(half * 0.2, 3).moveTo(half * 0.2, -1).lineTo(-half * 0.2, 3).stroke({ color: stoneLo(art), width: 1.2, alpha: 0.45 }); // crossed arms
  c.addChild(g);
  return c;
}

/** A small TASTEFUL BONE PILE — pale bones + a skull or two (cartoon, never gory). Catacombs ambience. */
export function bonePile(art: ThemeArt): Container {
  const c = new Container();
  void art;
  c.addChild(shadow(20, 7, 8));
  const g = new Graphics();
  const bone = 0xe8e2d2;
  const boneLo = darken(bone, 0.14);
  // a few long bones criss-crossed
  for (const [bx, by, ang] of [[-10, 4, 0.3], [8, 6, -0.4], [-2, 8, 0.1]] as const) {
    const dx = Math.cos(ang) * 12;
    const dy = Math.sin(ang) * 12;
    g.moveTo(bx - dx, by - dy).lineTo(bx + dx, by + dy).stroke({ color: bone, width: 4 });
    g.circle(bx - dx, by - dy, 2.4).fill(bone).circle(bx + dx, by + dy, 2.4).fill(bone); // knobby ends
    g.circle(bx - dx, by - dy, 2.4).stroke({ color: boneLo, width: 0.6, alpha: 0.5 });
  }
  // a skull (rounded cranium + jaw + two dark eye sockets)
  g.ellipse(-3, -2, 8, 7).fill(bone);
  g.ellipse(-5, -4, 4, 3).fill({ color: 0xffffff, alpha: 0.3 }); // lit cap
  g.rect(-7, 2, 8, 4).fill(boneLo); // jaw
  g.ellipse(-6, -1, 2, 2.4).fill(0x2a2620).ellipse(0, -1, 2, 2.4).fill(0x2a2620); // sockets
  g.moveTo(-3, 2).lineTo(-2, 4).lineTo(-4, 4).fill(0x2a2620); // nose
  // a smaller second skull behind
  g.ellipse(9, 3, 5, 4.5).fill(darken(bone, 0.06));
  g.circle(7.5, 2.5, 1.2).fill(0x2a2620).circle(10.5, 2.5, 1.2).fill(0x2a2620);
  c.addChild(g);
  return c;
}

/** A wall CRYPT NICHE — an arched recess holding a small urn + a votive candle (a tiny glow). Catacombs. */
export function cryptNiche(art: ThemeArt): Container {
  const c = new Container();
  glowPool(c, 0, -6, 22, art, 0.4);
  const g = new Graphics();
  // arched recess (the dark alcove cut into the wall)
  g.moveTo(-22, 18).lineTo(-22, -16).quadraticCurveTo(0, -40, 22, -16).lineTo(22, 18).fill(stone(art));
  g.moveTo(-17, 18).lineTo(-17, -14).quadraticCurveTo(0, -33, 17, -14).lineTo(17, 18).fill({ color: 0x18161e }); // dark interior
  g.moveTo(-17, -14).quadraticCurveTo(0, -33, 17, -14).quadraticCurveTo(0, -28, -17, -14).fill({ color: stoneHi(art), alpha: 0.25 }); // lit arch lip
  // a shelf line
  g.rect(-17, 6, 34, 2).fill(stoneLo(art));
  c.addChild(g);
  // an urn on the shelf
  const u = new Graphics();
  u.moveTo(-7, 6).quadraticCurveTo(-10, -8, -4, -12).lineTo(4, -12).quadraticCurveTo(10, -8, 7, 6).fill(mix(0x9a7a4a, art.accent, 0.1));
  u.ellipse(0, -12, 6, 2.4).fill(darken(mix(0x9a7a4a, art.accent, 0.1), 0.15));
  u.ellipse(-2, -2, 3, 5).fill({ color: 0xffffff, alpha: 0.12 }); // sheen
  c.addChild(u);
  // a votive candle beside it
  const f = new Graphics();
  f.roundRect(11, -8, 3, 12, 1).fill(0xf3e6c0);
  flame(f, 12.5, -8, 4);
  c.addChild(f);
  return c;
}

/** A prisoner's STRAW BED — a low straw pallet heaped on the floor. Dungeon cell dressing. */
export function strawBed(art: ThemeArt): Container {
  const c = new Container();
  void art;
  c.addChild(shadow(26, 8, 6));
  const g = new Graphics();
  const straw = 0xc7a14a;
  // the heaped pallet
  g.ellipse(0, 0, 26, 11).fill(darken(straw, 0.16));
  g.ellipse(0, -3, 24, 9).fill(straw);
  g.ellipse(-4, -5, 16, 5).fill({ color: lighten(straw, 0.14), alpha: 0.6 }); // lit top
  c.addChild(g);
  // scattered straw strands so it reads as straw, not a cushion
  const s = new Graphics();
  for (let i = 0; i < 22; i++) {
    const sx = -24 + ((i * 47) % 48);
    const sy = -7 + ((i * 23) % 12);
    const a = (i % 5) * 0.5 - 1;
    s.moveTo(sx, sy).lineTo(sx + Math.cos(a) * 6, sy + Math.sin(a) * 3).stroke({ color: i % 2 ? darken(straw, 0.18) : lighten(straw, 0.1), width: 1, alpha: 0.7 });
  }
  c.addChild(s);
  return c;
}

// ── TOWERS (astronomy / mage / alchemy / royal solar) ─────────────────────────────────────────────────

/** A brass TELESCOPE on a wooden tripod, tube angled up toward the sky. Astronomy tower. */
export function telescope(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(shadow(18, 6, 18));
  const brass = mix(0x9a7838, art.accent, 0.42);
  const g = new Graphics();
  // wooden tripod legs
  g.moveTo(0, -2).lineTo(-14, 18).moveTo(0, -2).lineTo(14, 18).moveTo(0, -2).lineTo(2, 18).stroke({ color: woodDk(art), width: 3 });
  g.circle(0, -2, 3).fill(iron()); // hub
  c.addChild(g);
  // the angled brass tube (pointing up-right toward the sky)
  const t = new Graphics();
  const ang = -0.62;
  const len = 46;
  const ex = Math.cos(ang) * len;
  const ey = -6 + Math.sin(ang) * len;
  t.moveTo(0, -6).lineTo(ex, ey).stroke({ color: brass, width: 11 }); // tube body
  t.moveTo(0, -6).lineTo(ex, ey).stroke({ color: lighten(brass, 0.3), width: 3, alpha: 0.6 }); // lit edge
  t.circle(ex, ey, 7).fill(darken(brass, 0.1)); // objective lens cap
  t.circle(ex, ey, 4.5).fill({ color: 0x1a2638, alpha: 0.9 }); // dark lens
  t.circle(ex - 1.5, ey - 1.5, 1.6).fill({ color: 0xffffff, alpha: 0.4 }); // glint
  t.circle(0, -6, 4).fill(darken(brass, 0.05)); // eyepiece
  // mount knob
  t.circle(0, -6, 2).fill(iron());
  c.addChild(t);
  // a couple of stars near the lens (the "looking up" tell)
  c.addChild(new Graphics().moveTo(ex + 8, ey - 14).lineTo(ex + 8, ey - 8).moveTo(ex + 5, ey - 11).lineTo(ex + 11, ey - 11).stroke({ color: 0xfff3c4, width: 1.2, alpha: 0.7 }).circle(ex + 16, ey - 4, 1).fill({ color: 0xfff3c4, alpha: 0.7 }));
  return c;
}

/** An ARMILLARY SPHERE — brass rings around a small globe on a stand. Astronomy ornament. */
export function armillary(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(shadow(16, 6, 18));
  const brass = mix(0x9a7838, art.accent, 0.42);
  const brassHi = lighten(brass, 0.3);
  const g = new Graphics();
  // turned wooden stand
  g.roundRect(-3, -2, 6, 18, 2).fill(woodDk(art));
  g.ellipse(0, 16, 11, 4).fill(woodDk(art));
  g.ellipse(0, 16, 7, 2.5).fill({ color: 0xffffff, alpha: 0.1 });
  c.addChild(g);
  // the brass rings around the centre
  const r = new Graphics();
  const cy = -16;
  const rad = 18;
  r.ellipse(0, cy, rad, rad).stroke({ color: brass, width: 2.5 }); // outer meridian
  r.ellipse(0, cy, rad, rad * 0.34).stroke({ color: brass, width: 2.5 }); // equator
  r.ellipse(0, cy, rad * 0.62, rad).stroke({ color: darken(brass, 0.1), width: 2 }); // tilted ring
  r.ellipse(0, cy, rad, rad).stroke({ color: brassHi, width: 1, alpha: 0.4 });
  r.moveTo(0, cy - rad - 4).lineTo(0, cy + rad + 4).stroke({ color: brass, width: 2 }); // axis
  r.circle(0, cy - rad - 4, 2).fill(brass).circle(0, cy + rad + 4, 2).fill(brass); // axis caps
  // the little globe at the centre
  bodyEllipse(r, 0, cy, 5, 5, mix(0x3a6a8a, art.accent, 0.1), true);
  r.ellipse(-1.5, cy - 1.5, 2, 2).fill({ color: 0xffffff, alpha: 0.35 });
  c.addChild(r);
  return c;
}

/** A glowing CRYSTAL ORB on an ornate stand (a cool magical glow pool). Mage tower. */
export function crystalOrb(art: ThemeArt): Container {
  const c = new Container();
  const magic = mix(0x6ad0e0, art.glow, 0.3);
  coolPool(c, 0, 0, 44, magic, 1);
  c.addChild(shadow(16, 6, 16));
  const brass = mix(0x9a7838, art.accent, 0.42);
  const g = new Graphics();
  // ornate three-clawed stand
  g.ellipse(0, 14, 13, 4).fill(darken(brass, 0.1));
  g.roundRect(-4, 2, 8, 12, 2).fill(brass);
  for (const dx of [-7, 0, 7]) g.moveTo(dx * 0.6, 0).quadraticCurveTo(dx, -4, dx, -10).stroke({ color: brass, width: 2.5 }); // claws cradling the orb
  c.addChild(g);
  // the glowing crystal ball
  const o = new Graphics();
  o.circle(0, -16, 16).fill({ color: magic, alpha: 0.22 }); // bloom
  o.circle(0, -16, 13).fill({ color: lighten(magic, 0.1), alpha: 0.85 }); // glass
  o.circle(0, -16, 13).stroke({ color: lighten(magic, 0.35), width: 1, alpha: 0.5 });
  o.circle(-4, -20, 4.5).fill({ color: 0xffffff, alpha: 0.55 }); // bright sheen
  o.circle(2, -12, 5).fill({ color: lighten(magic, 0.4), alpha: 0.4 }); // inner swirl
  // a couple of sparks inside
  o.circle(3, -18, 1.4).fill({ color: 0xffffff, alpha: 0.8 }).circle(-2, -13, 1).fill({ color: 0xffffff, alpha: 0.6 });
  c.addChild(o);
  return c;
}

/** Alchemical glassware (ALEMBIC): a retort-flask + a coiled condenser + bubbling COLORED liquid with a
 *  small glow and a tiny flame under it. Alchemy lab. */
export function alembic(art: ThemeArt): Container {
  const c = new Container();
  const brew = mix(0x4ad06a, art.glow, 0.2); // a luminous alchemical green (swap to violet by passing brew?)
  coolPool(c, 0, 8, 28, brew, 0.7);
  c.addChild(shadow(18, 6, 16));
  const g = new Graphics();
  const brass = mix(0x9a7838, art.accent, 0.42);
  // an iron ring-stand
  g.roundRect(-12, 4, 3, 14, 1).fill(iron());
  g.ellipse(-12, 18, 8, 3).fill(iron());
  g.moveTo(-12, -2).lineTo(2, -2).stroke({ color: iron(), width: 2 }); // clamp arm
  c.addChild(g);
  // the round-bottomed flask of bubbling brew
  const fl = new Graphics();
  fl.circle(0, 0, 12).fill({ color: 0xcfe6ee, alpha: 0.32 }); // glass
  fl.circle(0, 0, 12).stroke({ color: 0xffffff, alpha: 0.3 });
  fl.moveTo(0, 4).arc(0, 4, 9, 0, Math.PI).fill({ color: brew, alpha: 0.85 }); // liquid in the lower bulb
  fl.ellipse(0, 4, 9, 3).fill({ color: lighten(brew, 0.25), alpha: 0.7 }); // liquid surface
  fl.rect(-3, -22, 6, 12).fill({ color: 0xcfe6ee, alpha: 0.3 }).rect(-3, -22, 6, 12).stroke({ color: 0xffffff, alpha: 0.3 }); // neck
  fl.circle(-4, -4, 3).fill({ color: 0xffffff, alpha: 0.35 }); // sheen
  // rising bubbles
  for (const [bx, by, br] of [[-2, 0, 1.6], [3, -3, 1.2], [0, -8, 1], [-3, -14, 0.8]] as const) fl.circle(bx, by, br).fill({ color: lighten(brew, 0.4), alpha: 0.7 });
  c.addChild(fl);
  // a coiled brass condenser spiraling off the neck
  const coil = new Graphics();
  for (let i = 0; i < 5; i++) coil.ellipse(16 + i * 1.5, -16 + i * 5, 7, 3).stroke({ color: brass, width: 2 });
  coil.moveTo(3, -20).lineTo(14, -22).stroke({ color: brass, width: 2 }); // pipe to coil
  coil.moveTo(20, 6).lineTo(24, 12).stroke({ color: brass, width: 2 }); // drip spout
  c.addChild(coil);
  // tiny flame under the flask
  const f = new Graphics();
  flame(f, 0, 16, 8);
  c.addChild(f);
  return c;
}

/** A glowing arcane floor SIGIL (SPELL CIRCLE): concentric rings + rune ticks + a central star. Laid FLAT
 *  like a rug. Mage tower. */
export function spellCircle(art: ThemeArt, r = 80): Container {
  const c = new Container();
  const magic = mix(0x7a6ad0, art.glow, 0.4); // an arcane violet-blue
  coolPool(c, 0, 0, r * 1.05, magic, 0.85);
  const g = new Graphics();
  // concentric rings
  g.circle(0, 0, r).stroke({ color: magic, width: 2.5, alpha: 0.6 });
  g.circle(0, 0, r * 0.82).stroke({ color: lighten(magic, 0.2), width: 1.4, alpha: 0.45 });
  g.circle(0, 0, r * 0.5).stroke({ color: magic, width: 2, alpha: 0.55 });
  g.circle(0, 0, r * 0.34).stroke({ color: lighten(magic, 0.25), width: 1.2, alpha: 0.5 });
  // rune ticks between the two outer rings
  for (let i = 0; i < 16; i++) {
    const a = (i / 16) * Math.PI * 2;
    const x0 = Math.cos(a) * r * 0.82;
    const y0 = Math.sin(a) * r * 0.82;
    const x1 = Math.cos(a) * r;
    const y1 = Math.sin(a) * r;
    g.moveTo(x0, y0).lineTo(x1, y1).stroke({ color: lighten(magic, 0.15), width: 1.2, alpha: 0.4 });
    // a tiny rune glyph dab at every other tick
    if (i % 2 === 0) g.circle(Math.cos(a) * r * 0.91, Math.sin(a) * r * 0.91, 2).fill({ color: lighten(magic, 0.3), alpha: 0.55 });
  }
  // a central five-point star
  const star = new Graphics();
  const pts: number[] = [];
  for (let i = 0; i < 5; i++) {
    const a = -Math.PI / 2 + (i / 5) * Math.PI * 2;
    pts.push(Math.cos(a) * r * 0.34, Math.sin(a) * r * 0.34);
  }
  // draw the pentagram by connecting every other point
  for (let i = 0; i < 5; i++) {
    const a = pts[(i * 2) % 5 * 2]!;
    const b = pts[(i * 2) % 5 * 2 + 1]!;
    const cX = pts[((i * 2 + 2) % 5) * 2]!;
    const cY = pts[((i * 2 + 2) % 5) * 2 + 1]!;
    star.moveTo(a, b).lineTo(cX, cY).stroke({ color: lighten(magic, 0.35), width: 2, alpha: 0.7 });
  }
  star.circle(0, 0, 4).fill({ color: 0xffffff, alpha: 0.5 }); // a bright core
  c.addChild(g);
  c.addChild(star);
  return c;
}

/** A grand canopied royal FOUR-POSTER BED: four posts + a draped canopy + a cream mattress + a red/gold
 *  coverlet + pillows. Royal tower / solar. */
export function fourPosterBed(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(shadow(46, 14, 26));
  const g = new Graphics();
  const fr = wood(art);
  const coverlet = mix(0x8a2433, art.accent, 0.12);
  // back posts (taller) + a head canopy rail
  for (const px of [-42, 42]) {
    g.roundRect(px - 3.5, -64, 7, 90, 2).fill(fr);
    g.roundRect(px - 3.5, -64, 7, 8, 2).fill({ color: 0xffffff, alpha: 0.12 });
    g.circle(px, -66, 4).fill(gold(art)); // finial
  }
  // the draped canopy across the top
  g.roundRect(-46, -64, 92, 12, 3).fill(coverlet);
  g.roundRect(-46, -64, 92, 4, 3).fill({ color: 0xffffff, alpha: 0.14 });
  for (let x = -42; x < 44; x += 12) g.poly([x, -52, x + 6, -44, x + 12, -52]).fill({ color: darken(coverlet, 0.08), alpha: 0.9 }); // scalloped valance
  g.rect(-46, -56, 92, 2).fill({ color: gold(art), alpha: 0.6 }); // gold trim
  c.addChild(g);
  // the bed body: headboard + cream mattress + coverlet + pillows
  const b = new Graphics();
  b.roundRect(-42, -34, 84, 16, 3).fill(woodDk(art)); // headboard
  b.roundRect(-40, -32, 80, 5, 2).fill({ color: gold(art), alpha: 0.4 });
  bodyRect(b, -42, -20, 84, 44, 4, 0xe7ddc4); // cream mattress
  // coverlet over the lower 2/3
  b.roundRect(-42, -2, 84, 26, 4).fill(coverlet);
  b.roundRect(-42, -2, 84, 6, 4).fill({ color: 0xffffff, alpha: 0.12 });
  b.rect(-42, 8, 84, 2).fill({ color: gold(art), alpha: 0.45 }); // a gold band
  // two plump pillows at the head
  for (const px of [-22, 12]) {
    b.roundRect(px, -18, 28, 13, 5).fill(0xf6efdc);
    b.roundRect(px, -18, 28, 4, 5).fill({ color: 0xffffff, alpha: 0.4 });
  }
  c.addChild(b);
  return c;
}
